import React, { useEffect, useState } from 'react';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Input from '../components/atoms/Input';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';

// Simulated API fetch for marketplace data
const fetchMarketplaceData = async () => {
  return Promise.resolve({
    categories: [
      { icon: 'group', name: 'HR & Employee Management', products: 12 },
      { icon: 'gavel', name: 'LegalTech & Compliance', products: 18 },
      { icon: 'currency_rupee', name: 'Finance & Operations', products: 15 },
      { icon: 'apartment', name: 'Corporate Services', products: 10 },
      { icon: 'cloud', name: 'IT & Cloud Tools', products: 20 }
    ],
    tools: [
      { name: 'Keka HR', badge: 'Popular', role: 'HRMS Software', segment: 'SMEs & Enterprises',
        intro: 'Complete HR management solution with payroll, attendance, and performance management',
        features: ['Payroll Processing', 'Attendance Tracking', 'Performance Management', 'Leave Management'],
        price: 'Starting ₹4,999/month', cta: 'Contact for Deal' },
      { name: 'SpotDraft', badge: 'Featured', role: 'Contract Management', segment: 'Startups & Enterprises',
        intro: 'AI-powered contract lifecycle management for Indian businesses',
        features: ['Contract Drafting', 'Automated Workflows', 'E-Signatures', 'Compliance Tracking'],
        price: 'Custom Pricing', cta: 'Contact for Deal' },
      { name: 'Zoho Books', badge: 'Top Rated', role: 'Accounting Software', segment: 'All Business Sizes',
        intro: 'GST-compliant accounting software designed for Indian businesses',
        features: ['GST Filing', 'Invoice Management', 'Expense Tracking', 'Bank Reconciliation'],
        price: 'Starting ₹1,499/month', cta: 'Contact for Deal' },
      { name: 'GreytHR', badge: 'Popular', role: 'Payroll Management', segment: 'SMEs',
        intro: 'Cloud-based payroll and HR software for Indian companies',
        features: ['Statutory Compliance', 'Payroll Automation', 'Employee Self-Service', 'Reports'],
        price: 'Starting ₹2,999/month', cta: 'Contact for Deal' },
      { name: 'Legistify', badge: 'Featured', role: 'Legal Services', segment: 'Startups & Enterprises',
        intro: 'End-to-end legal service platform for businesses',
        features: ['Legal Documentation', 'Compliance Management', 'Trademark Filing', 'Contract Review'],
        price: 'Starting ₹9,999/month', cta: 'Contact for Deal' },
      { name: 'AWS India', badge: 'Enterprise', role: 'Cloud Hosting', segment: 'Enterprises',
        intro: 'Scalable cloud infrastructure with India data centers',
        features: ['Compute Services', 'Storage Solutions', 'Database Management', 'Security'],
        price: 'Pay as you go', cta: 'Contact for Deal' }
    ],
    services: [
      { title: 'MSME Registration', desc: 'Complete registration services for Micro, Small, and Medium Enterprises', price: '₹4,999 onwards' },
      { title: 'GST Registration', desc: 'Quick and hassle-free GST registration with expert support', price: '₹2,999 onwards' },
      { title: 'Trademark Filing', desc: 'Protect your brand with comprehensive trademark registration', price: '₹6,999 onwards' },
      { title: 'Background Verification', desc: 'Comprehensive employee verification services across India', price: '₹399 per check' },
      { title: 'POSH Compliance', desc: 'Complete Prevention of Sexual Harassment compliance solutions', price: '₹14,999 onwards' },
      { title: 'Corporate Training', desc: 'Customized training programs for your team', price: 'Custom Pricing' }
    ]
  });
};

const heroBg = 'linear-gradient(120deg, rgba(4,33,90,0.65),rgba(13,110,253,0.55)), url("https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?auto=format&fit=crop&w=1600&q=80")';

const MarketplacePage = () => {
  const [loading, setLoading] = useState(true);
  const [marketData, setMarketData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    fetchMarketplaceData().then((data) => {
      if (isMounted) {
        setMarketData(data);
        setLoading(false);
      }
    });
    return () => { isMounted = false; };
  }, []);

  if (loading || !marketData) {
    return <div className="min-h-screen flex items-center justify-center text-lg">Loading Marketplace...</div>;
  }

  return (
    <div className="relative min-h-screen flex flex-col bg-background-light dark:bg-background-dark">
      <Header />
      <main>
        {/* Hero Banner */}
        <section className="relative">
          <div
            className="w-full bg-cover bg-center"
            style={{ backgroundImage: heroBg }}
          >
            <div className="mx-auto flex min-h-[420px] max-w-6xl flex-col justify-center px-4 py-16 text-white sm:px-6 lg:px-8">
              <span className="inline-flex items-center rounded-full bg-white/20 px-4 py-1 text-sm font-semibold backdrop-blur">B2B Marketplace</span>
              <h1 className="mt-6 text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                Corporate Solutions<br /> <span className="text-primary">For Indian Businesses</span>
              </h1>
              <p className="mt-4 text-lg text-white/90 sm:text-xl">
                One-stop marketplace for SaaS, services, and corporate solutions tailored for India
              </p>
              <div className="mt-8 w-full max-w-3xl rounded-2xl bg-white/90 p-4 shadow-lg backdrop-blur">
                <Input
                  placeholder="Search for products, services, categories..."
                  className="h-14 border border-gray-200 text-gray-800"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="bg-white py-12 dark:bg-background-dark">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">Browse by Category</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              Find the right solution for your business needs
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {marketData.categories.map((cat, i) => (
                <div
                  key={cat.name}
                  className="flex flex-col items-center justify-center rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 w-56 h-44 shadow-sm hover:shadow-md transition"
                >
                  <div className="flex items-center justify-center rounded-full bg-primary/10 text-primary size-14 text-3xl mb-3">
                    <Icon name={cat.icon} />
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white mb-0 leading-tight">{cat.name}</p>
                  <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">{cat.products} Products</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured SaaS & Tools */}
        <section className="bg-[#f4f8ff] py-16 dark:bg-gray-900/80">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Featured SaaS & Tools</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Top-rated solutions trusted by Indian businesses</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {marketData.tools.map((tool, i) => (
                <div
                  key={tool.name}
                  className="flex flex-col p-7 rounded-3xl bg-white shadow-lg dark:bg-gray-950 dark:shadow-black/25 border border-gray-100 dark:border-gray-800"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl font-bold text-gray-900 dark:text-white">{tool.name}</span>
                    <span className="ml-auto rounded-full bg-blue-50 text-primary text-xs px-3 py-1 font-semibold">{tool.badge}</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-300 mb-1">{tool.role}</div>
                  <div className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">{tool.segment}</div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{tool.intro}</p>
                  <ul className="mb-3 ml-3 list-disc text-xs text-green-700 dark:text-green-300">
                    {tool.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                  <div className="mt-auto flex flex-col gap-2">
                    <span className="pt-2 text-base font-bold text-primary">{tool.price}</span>
                    <Button variant="secondary" className="w-full">{tool.cta}</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Corporate Services */}
        <section className="bg-white py-16 dark:bg-background-dark">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">Corporate Services</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">Essential services for compliance and growth</p>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {marketData.services.map((svc) => (
                <div
                  key={svc.title}
                  className="flex flex-col items-start justify-between rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 p-6 text-left shadow-sm"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{svc.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{svc.desc}</p>
                  <div className="mt-auto pt-4 flex w-full items-center justify-between">
                    <p className="text-primary font-bold">{svc.price}</p>
                    <Button variant="outline">Learn More</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Vendor CTA */}
        <section className="py-16">
          <div className="mx-auto max-w-4xl rounded-3xl border border-primary/20 bg-white p-10 text-center shadow-lg dark:bg-gray-900">
            <div className="mx-auto flex size-20 items-center justify-center rounded-full bg-primary/10 text-primary text-4xl">
              <Icon name="apartment" />
            </div>
            <h3 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">
              Become a Marketplace Vendor
            </h3>
            <p className="mt-3 text-gray-600 dark:text-gray-400">
              List your product or service and reach thousands of Indian businesses looking for solutions
            </p>
            <div className="mt-6 flex justify-center gap-4 flex-wrap">
              <Button>List Your Product</Button>
              <Button variant="secondary">Learn About Vendor Program</Button>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-16 bg-[#f4f8ff] dark:bg-gray-900/60 text-center">
          <div className="mx-auto max-w-6xl gap-12 grid grid-cols-2 md:grid-cols-4">
            <div className="flex flex-col items-center gap-2">
              <div className="rounded-full bg-primary/10 text-primary text-3xl size-16 flex items-center justify-center mb-2">
                <Icon name="verified_user" />
              </div>
              <p className="font-bold text-base">Verified Vendors</p>
              <p className="text-xs text-gray-600">Only trusted, verified partners</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="rounded-full bg-primary/10 text-primary text-3xl size-16 flex items-center justify-center mb-2">
                <Icon name="currency_rupee" />
              </div>
              <p className="font-bold text-base">India Pricing</p>
              <p className="text-xs text-gray-600">Transparent INR pricing</p>
            </div>
            <div className="flex flex-col items-center gap-2 ">
              <div className="rounded-full bg-primary/10 text-primary text-3xl size-16 flex items-center justify-center mb-2">
                <Icon name="quiz" />
              </div>
              <p className="font-bold text-base">Easy Comparison</p>
              <p className="text-xs text-gray-600">Compare features and prices</p>
            </div>
            <div className="flex flex-col items-center gap-2 ">
              <div className="rounded-full bg-primary/10 text-primary text-3xl size-16 flex items-center justify-center mb-2">
                <Icon name="groups" />
              </div>
              <p className="font-bold text-base">Expert Support</p>
              <p className="text-xs text-gray-600">Dedicated assistance</p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MarketplacePage;
